import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useClientModelVariables } from "@services/api";
import { ClientModelVariable, ModelScenarioInput } from "@services/api/types";
import { getLastMediaVarId } from "@utils/media_vars";
import { toLocaleString } from "@utils/numbers";
import { TFunction } from "i18next";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

const getTimePeriodVarValue = (
  model_id: string,
  varName: string,
  timePeriods: string[],
  inputs: ModelScenarioInput[]
): JSX.Element[] => {
  const varInputs = inputs.filter((input) => input.name === varName);
  let periodTotal = 0;

  const periodValues = timePeriods.map((timePeriod) => {
    const value = varInputs.find(
      (input) => input.time_period === timePeriod
    )?.value;
    periodTotal += value || 0;

    return (
      <StyledTableCell
        align="center"
        key={`${model_id}-${varName}-${timePeriod}`}
      >
        {toLocaleString(value || 0, 0)}
      </StyledTableCell>
    );
  });
  periodValues.push(
    <StyledTableCell align="center" key={`${model_id}-${varName}-total`}>
      {toLocaleString(periodTotal, 0)}
    </StyledTableCell>
  );
  return periodValues;
};

const getTableRow = (
  model_id: string,
  modelVar: ClientModelVariable,
  errorColor: string,
  inputs: ModelScenarioInput[],
  timePeriods: string[]
): JSX.Element => {
  return (
    <StyledTableRow
      key={`${model_id}-${modelVar.name}`}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 }
      }}
    >
      <StyledTableCell align="center">
        <Box className="flex">
          <span
            className="dot"
            style={{
              backgroundColor: modelVar.color,
              height: 10,
              width: 10,
              borderRadius: "50%",
              marginTop: 5,
              marginRight: 4
            }}
          ></span>
          {modelVar.name}
          <Box
            sx={{
              color: errorColor,
              fontWeight: 600
            }}
          >
            {!modelVar.is_significant ? "*" : ""}
          </Box>
        </Box>
      </StyledTableCell>
      {getTimePeriodVarValue(model_id, modelVar.name, timePeriods, inputs)}
    </StyledTableRow>
  );
};

const getTotalRow = (
  t: TFunction<"translation", undefined>,
  model_id: string,
  modelVars: ClientModelVariable[],
  inputs: ModelScenarioInput[],
  timePeriods: string[]
): JSX.Element => {
  const mediaVarsNames = modelVars.map(({ name }) => name);
  const totalValues = inputs.filter((input) =>
    mediaVarsNames.includes(input.name)
  );
  const totalValue = totalValues.reduce((acc, period) => acc + period.value, 0);
  return (
    <StyledTableRow
      key={`${model_id}-total`}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 }
      }}
    >
      <StyledTableCell align="left">
        {t("simulator.scenario.total")}
      </StyledTableCell>
      {timePeriods.map((timePeriod) => {
        const timePeriodValues = inputs.filter(
          (input) =>
            input.time_period === timePeriod &&
            mediaVarsNames.includes(input.name)
        );
        const timePeriodValue = timePeriodValues.reduce(
          (acc, period) => acc + period.value,
          0
        );
        return (
          <StyledTableCell
            align="center"
            key={`${model_id}-total-${timePeriod}`}
          >
            {toLocaleString(timePeriodValue || 0, 0)}
          </StyledTableCell>
        );
      })}
      <StyledTableCell align="center" key={`${model_id}-total-total`}>
        {toLocaleString(totalValue || 0, 0)}
      </StyledTableCell>
    </StyledTableRow>
  );
};

export interface ExternalVariablesProps {
  inputs: ModelScenarioInput[];
  timePeriods: string[];
}

const MediaVariables = ({ inputs, timePeriods }: ExternalVariablesProps) => {
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconVisibility = isMobile ? "" : "hidden";
  const iconJustify = isMobile ? "justify-left" : "";

  const { data: modelVars, isFetching: isLoading } = useClientModelVariables(
    currentClient.model.id
  );

  const timePeriodHeader = useMemo(() => {
    return timePeriods.map((timePeriod) => (
      <StyledTableCell align="center" key={`scenario-media-var-${timePeriod}`}>
        {timePeriod}
      </StyledTableCell>
    ));
  }, [timePeriods]);

  const scenarioMediaVars: ClientModelVariable[] = useMemo(() => {
    const modelMediaVars = (modelVars || []).filter(
      (modelVar) => modelVar.type === "media"
    );
    const externalVars = (modelVars || []).filter(
      (modelVar) => modelVar.type === "external"
    );

    const lastIndex = getLastMediaVarId(modelMediaVars);
    const inputsMediaVars = inputs.map((input) => input.name);
    const customMediaVars = Array.from(new Set(inputsMediaVars))
      .filter(
        (inputMediaVar) =>
          !modelMediaVars.some((modelVar) => modelVar.name === inputMediaVar) &&
          !externalVars.some((modelVar) => modelVar.name === inputMediaVar)
      )
      .map(
        (customMediaVar, idx) =>
          ({
            id: lastIndex + idx,
            name: customMediaVar,
            is_significant: false,
            order: lastIndex + idx,
            type: "media",
            color: theme.palette.warning.main,
            input_type: "input"
          }) as ClientModelVariable
      );

    return [...modelMediaVars, ...customMediaVars];
  }, [modelVars]);

  const tableRows = useMemo(() => {
    const mediaRows = scenarioMediaVars.map((modelVar) =>
      getTableRow(
        currentClient.model.id.toString(),
        modelVar,
        theme.palette.error.main,
        inputs,
        timePeriods
      )
    );

    mediaRows.push(
      getTotalRow(
        t,
        currentClient.model.id.toString(),
        scenarioMediaVars,
        inputs,
        timePeriods
      )
    );
    return mediaRows;
  }, [
    t,
    theme.palette.error,
    currentClient.model.id,
    inputs,
    scenarioMediaVars,
    timePeriods
  ]);

  return (
    <Card className="w-full mt-8">
      <CardContent>
        <Typography variant="h6" component="div">
          {t("simulator.scenario.media_variables")}
        </Typography>
        <Box className={`w-full flex pt-4 ${iconVisibility} ${iconJustify}`}>
          <SwipeIcon
            sx={{
              bottom: 12,
              opacity: 0.4,
              left: "50%",
              transform: "translate(750%, 0%)",
              color: theme.palette.primary.main
            }}
            fontSize="small"
          />
        </Box>
        <TableContainer
          className="w-full p-4 overflow-y-scroll"
          sx={{
            maxHeight: "calc(100vh - 450px)",
            minHeight: 300,
            overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
            scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
            "&::-webkit-scrollbar": {
              display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
            }
          }}
        >
          <Table
            className="w-full"
            aria-label="scenario media vars table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                {inputs.length > 0 && (
                  <StyledTableCell align="left">
                    {t("simulator.scenario.table.media_variables.channel")}
                  </StyledTableCell>
                )}
                {timePeriodHeader}
                {inputs.length > 0 && (
                  <StyledTableCell align="center">
                    {t("simulator.scenario.total")}
                  </StyledTableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || inputs.length === 0 ? (
                <TableRowsSkeleton
                  id={`${currentClient.model.id}-media-variables-skeleton`}
                  numberOfRows={5}
                  columns={6}
                />
              ) : (
                tableRows
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box className="w-full flex p-4">
          <Box sx={{ color: theme.palette.error.main, fontWeight: 600 }}>*</Box>
          <Box sx={{ fontStyle: "italic" }}>
            {t("simulator.scenario.significant_variables", {
              kpi: currentClient.client.kpi_label
            })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
export default MediaVariables;
